import type { HttpClient } from '../api/httpClient';
import type {
  Category,
  CostCenter,
  MenuItem,
  OrderDraft,
  OrderQuote,
  OrderSubmissionOptions,
  SubmittedOrder,
} from '../types/catering';
import { addPrices, multiplyPrice, zeroPrice } from '../utils/price';
import {
  CateringServiceError,
  getDeliveryWindows,
  type CateringService,
  type MenuFilter,
} from './cateringService';

const ORDER_ENVIRONMENT = 'BANQ' as const;
const ORDER_DELIVERY_METHOD = 'Delivery' as const;

type ProductCategoryResponse = {
  readonly id: number;
  readonly name: string;
  readonly description: string;
};

type CostCenterResponse = CostCenter & {
  readonly accessibleToAllAppUsers: boolean;
};

type OutletResponse = {
  readonly id: number;
};

type CreatedOrderResponse = {
  readonly retrievalCode: string;
};

type CreateOrderPayload = {
  readonly environment: typeof ORDER_ENVIRONMENT;
  readonly deliveryMethod: typeof ORDER_DELIVERY_METHOD;
  readonly tsDeliveryDesired: number;
  readonly outletId: number;
  readonly internalPeopleCount: number;
  readonly location: string;
  readonly orderLines: readonly {
    readonly type: 'PRODUCT';
    readonly id: number;
    readonly amount: number;
  }[];
  readonly note?: string;
  readonly payment: {
    readonly method: 'cost_center';
    readonly amount: number;
    readonly data: {
      readonly costCenterId: number;
    };
  };
};

/**
 * API-backed service composition point. The authenticated HTTP client is
 * injected here so the BFF contract can be added without changing pages or
 * providers.
 */
export function createCateringApiService(api: HttpClient): CateringService {
  let outletId: string | undefined;
  const menuItems = new Map<number, MenuItem>();
  let menuLoadPromise: Promise<readonly MenuItem[]> | undefined;

  async function getOutletContext(room: string) {
    if (room.trim().length === 0) {
      throw new CateringServiceError('A room is required to load catering options.', 'unavailable');
    }

    const outlet = await api.get<OutletResponse>(`/outlets?room=${encodeURIComponent(room)}`);
    outletId = String(outlet.id);

    return { outletId };
  }

  async function getCategories(): Promise<Category[]> {
    const categories = await api.get<readonly ProductCategoryResponse[]>('/product-categories');

    return categories.map(({ id, name }) => ({ id, name }));
  }

  async function getCostCenters(): Promise<CostCenter[]> {
    const costCenters = await api.get<readonly CostCenterResponse[]>('/cost-centers');

    return costCenters.map(({ id, name, code }) => ({ id, name, code }));
  }

  async function getMenu(filter?: MenuFilter): Promise<MenuItem[]> {
    if (outletId === undefined) {
      throw new CateringServiceError('The outlet has not been resolved yet.', 'unavailable');
    }

    const path = `/outlets/${outletId}/products`;
    const query = new URLSearchParams();
    if (filter?.categoryId !== undefined) query.set('categoryId', String(filter.categoryId));
    if (filter?.tsDeliveryDesired !== undefined) {
      query.set('tsDeliveryDesired', String(filter.tsDeliveryDesired));
    }
    const pathWithFilter = query.size === 0 ? path : `${path}?${query.toString()}`;

    const load = api.get<MenuItem[]>(pathWithFilter).then((items) => {
      items.forEach((item) => menuItems.set(item.id, item));
      return items;
    });
    menuLoadPromise = load;

    try {
      return await load;
    } finally {
      if (menuLoadPromise === load) menuLoadPromise = undefined;
    }
  }

  async function getMenuItem(itemId: number): Promise<MenuItem> {
    if (outletId === undefined) {
      throw new CateringServiceError('The outlet has not been resolved yet.', 'unavailable');
    }

    const item = await api.get<MenuItem>(`/outlets/${outletId}/products/${itemId}`);
    menuItems.set(item.id, item);
    return item;
  }

  async function quoteOrder(draft: OrderDraft): Promise<OrderQuote> {
    if (menuLoadPromise !== undefined) await menuLoadPromise;

    const pricedItems = draft.lines.map((line) => {
      const menuItem = menuItems.get(line.itemId);
      if (!menuItem) {
        throw new CateringServiceError(`Cannot quote unknown menu item ${line.itemId}.`, 'not_found');
      }

      const totalPortions = line.portionsPerPerson * draft.setup.headcount;
      return {
        itemId: menuItem.id,
        name: menuItem.name,
        portionsPerPerson: line.portionsPerPerson,
        lineTotal: multiplyPrice(
          { minorUnits: Math.round(menuItem.priceInfo.grossUnitCosts.Delivery * 100), currency: 'GBP' },
          totalPortions,
        ),
        netTotal: multiplyPrice(
          { minorUnits: Math.round(menuItem.priceInfo.netUnitCosts.Delivery * 100), currency: 'GBP' },
          totalPortions,
        ),
      };
    });
    const items = pricedItems.map((item) => ({
      itemId: item.itemId,
      name: item.name,
      portionsPerPerson: item.portionsPerPerson,
      lineTotal: item.lineTotal,
    }));
    const subtotal = pricedItems.reduce(
      (total, item) => addPrices(total, item.netTotal),
      zeroPrice('GBP'),
    );
    const grossTotal = pricedItems.reduce(
      (total, item) => addPrices(total, item.lineTotal),
      zeroPrice('GBP'),
    );
    const vat = addPrices(grossTotal, multiplyPrice(subtotal, -1));

    return {
      items,
      subtotal,
      deliveryAndSetup: zeroPrice('GBP'),
      vat,
      total: grossTotal,
    };
  }

  async function submitOrder(
    draft: OrderDraft,
    options?: OrderSubmissionOptions,
  ): Promise<SubmittedOrder> {
    if (outletId === undefined) {
      throw new CateringServiceError('The outlet has not been resolved yet.', 'unavailable');
    }
    if (options === undefined) {
      throw new CateringServiceError('Order submission options are required.', 'unavailable');
    }

    const response = await api.post<CreatedOrderResponse>('/orders', {
      environment: ORDER_ENVIRONMENT,
      deliveryMethod: ORDER_DELIVERY_METHOD,
      tsDeliveryDesired: draft.setup.window.tsDeliveryDesired,
      outletId: Number(outletId),
      internalPeopleCount: draft.setup.headcount,
      location: draft.setup.room,
      orderLines: draft.lines.map((line) => ({
        type: 'PRODUCT',
        id: line.itemId,
        amount: line.portionsPerPerson * draft.setup.headcount,
      })),
      note: options.comment,
      payment: {
        method: 'cost_center',
        amount: options.quote.total.minorUnits / 100,
        data: { costCenterId: options.costCenterId },
      },
    } satisfies CreateOrderPayload);

    return { retrievalCode: response.retrievalCode };
  }

  return {
    getOutletContext,
    getCategories,
    getMenu,
    getMenuItem,
    getDeliveryWindows,
    getCostCenters,
    quoteOrder,
    submitOrder,
  };
}
